import { DirectoryInput, Input, SelectInput, type SelectOption } from "@repo/ui";
import type { FieldData, FieldType, FieldValue } from "../types";
import { SaveCancelActions } from "./save-cancel-actions";

type Props = {
  field: FieldData;
  value: FieldValue;
  apiBaseUrl: string;
  onValueChange: (fieldId: number, value: FieldValue) => void;
  onSave: (fieldId: number) => void;
  onCancel: () => void;
};

const FieldTypeObject = {
  String: "String",
  Integer: "Integer",
  Directory: "Directory",
} as const;

export const FieldValueEditor = ({
  field,
  value,
  apiBaseUrl,
  onValueChange,
  onSave,
  onCancel,
}: Props) => {
  const renderEditor = () => {
    if (
      Object.values(FieldTypeObject).includes(field.type as FieldType) &&
      (field.options?.length ?? 0) > 0
    ) {
      const selectOptions: SelectOption<string>[] =
        field.options?.map((option) => ({
          value: option.value,
          label: option.value,
          disabled: false,
        })) ?? [];

      return (
        <SelectInput
          key={field.id}
          options={selectOptions}
          value={value != null ? String(value) : undefined}
          className="font-medium"
          onChange={(val: string | string[] | undefined) => {
            if (typeof val === "string") {
              onValueChange(field.id, val);
            } else if (val === undefined) {
              onValueChange(field.id, "");
            }
          }}
          required={field.required}
          clearable={!field.required}
        />
      );
    }

    switch (field.type as FieldType) {
      case FieldTypeObject.String:
        return (
          <Input
            type="text"
            value={value}
            inputSize="sm"
            className="font-medium"
            onChange={(e) => onValueChange(field.id, e.target.value)}
            autoFocus
          />
        );

      case FieldTypeObject.Integer:
        return (
          <Input
            type="number"
            value={value}
            inputSize="sm"
            className="font-medium"
            onChange={(e) => onValueChange(field.id, Number(e.target.value))}
            autoFocus
          />
        );

      case FieldTypeObject.Directory:
        return (
          <DirectoryInput
            apiBaseUrl={apiBaseUrl}
            value={String(value)}
            onChange={(newPath) => onValueChange(field.id, newPath)}
            placeholder="Select a folder..."
          />
        );

      default:
        return <span className="text-sm text-gray-900">{field.values}</span>;
    }
  };

  return (
    <div className="flex items-center gap-2">
      {renderEditor()}
      <SaveCancelActions fieldId={field.id} handleSaveClick={onSave} handleCancelClick={onCancel} />
    </div>
  );
};
